import type { ApiShift } from '@/api/data';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Button } from '@/ui';
import { fmtDs, shiftSpan } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useEffect, useRef, useState } from 'react';

export function overlapping(
  shifts: ApiShift[],
  id: number,
  userId: number | null,
  start: string,
  end: string,
) {
  if (userId == null) return [];
  return shifts.filter(
    (s) =>
      s.id !== id &&
      s.user_id === userId &&
      Date.parse(s.start_time) < Date.parse(end) &&
      Date.parse(s.end_time) > Date.parse(start),
  );
}
export function alternatives(
  shifts: ApiShift[],
  id: number,
  userId: number | null,
  start: string,
  end: string,
) {
  const hits = overlapping(shifts, id, userId, start, end);
  if (!hits.length) return [];
  const duration = Date.parse(end) - Date.parse(start);
  const candidates = [
    Math.min(...hits.map((s) => Date.parse(s.start_time))) - duration,
    Math.max(...hits.map((s) => Date.parse(s.end_time))),
  ];
  return candidates.flatMap((ms) => {
    const a = new Date(ms).toISOString();
    const b = new Date(ms + duration).toISOString();
    return oslo(a)?.date === oslo(start)?.date && !overlapping(shifts, id, userId, a, b).length
      ? [{ start: a, end: b }]
      : [];
  });
}
const label = (start: string, end: string) => `${fmtDs(oslo(start)?.date ?? '')}, ${shiftSpan(start, end)}`;
export function MoveConflict({
  id,
  userId,
  start,
  end,
  shifts,
  onApply,
  onEdit,
  onClose,
}: {
  id: number;
  userId: number | null;
  start: string;
  end: string;
  shifts: ApiShift[];
  onApply: (a: string, b: string) => Promise<boolean>;
  onEdit: () => void;
  onClose: () => void;
}) {
  const head = useRef<HTMLHeadingElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  useEffect(() => head.current?.focus({ preventScroll: true }), []);
  const hits = overlapping(shifts, id, userId, start, end);
  const options = alternatives(shifts, id, userId, start, end);
  return (
    <aside className="vpanel" aria-labelledby="move-conflict-title" data-testid="move-conflict">
      <div className="vphd">
        <div>
          <h3 id="move-conflict-title" tabIndex={-1} ref={head}>
            {T('Vakten overlapper')}
          </h3>
          <span>{label(start, end)}</span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} disabled={busy} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <p>{T('Ingen endring er lagret. Den ansatte har allerede en vakt i tidsrommet.')}</p>
      {hits.map((s) => (
        <div className="al warn" key={s.id}>
          {label(s.start_time, s.end_time)}
        </div>
      ))}
      <div className="vgroup">
        <h4>{T('Andre tidspunkter')}</h4>
        <p className="hint">
          {T('Samme lengde, uten overlapp i den innleste planen. Øvrige regler kontrolleres ved lagring.')}
        </p>
        {options.length ? (
          options.map((o) => (
            <Button
              key={o.start}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setProblem('');
                try {
                  if (!(await onApply(o.start, o.end)))
                    setProblem(
                      T('Endringen ble ikke bekreftet. Se meldingen og prøv igjen når planen er oppdatert.'),
                    );
                } catch {
                  setProblem(T('Endringen kunne ikke bekreftes. Oppdater planen før du prøver igjen.'));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {label(o.start, o.end)}
            </Button>
          ))
        ) : (
          <p>
            {T('Ingen ledige alternativer funnet denne dagen. Rediger tidspunkt eller medarbeider selv.')}
          </p>
        )}
      </div>
      {problem && (
        <p role="alert" className="hint warn">
          {problem}
        </p>
      )}
      <div className="vfoot">
        <Button disabled={busy} onClick={onClose}>
          {T('Behold opprinnelig vakt')}
        </Button>
        <Button disabled={busy} onClick={onEdit}>
          <Icon name="calendar" />
          {T('Rediger vakten')}
        </Button>
      </div>
    </aside>
  );
}
